/**
 * Minimal third-party accessibility patches only.
 * Do not override native list/tab roles on theme markup.
 */
(function () {
  function fixCartLineItemImageAlts(root) {
    const scope = root || document;

    scope.querySelectorAll('.cart-item__image, img.cart__image').forEach(function (img) {
      const alt = (img.getAttribute('alt') || '').trim();
      if (alt) return;

      const row = img.closest('.cart-item, tr.cart-item, [id^="CartItem-"], [id^="CartDrawer-Item-"], [id^="CartPopup-Item-"]');
      const titleLink = row?.querySelector('.cart-item__name');
      const label = titleLink?.textContent?.trim() || 'Cart item';
      img.setAttribute('alt', label);
    });
  }

  function enhanceMerchantWidgetIframe() {
    const iframe = document.getElementById('merchantwidgetiframe');
    if (!iframe) return;

    const title = iframe.getAttribute('title');
    const ariaLabel = iframe.getAttribute('aria-label');
    if ((!title || !title.trim()) && (!ariaLabel || !ariaLabel.trim())) {
      iframe.setAttribute('title', 'Google customer reviews');
    }
  }

  function fixEasytabsEmbeddedRegions(root) {
    const scope = root || document;

    scope.querySelectorAll('.easytabs-content-holder [role="region"]').forEach(function (el) {
      el.setAttribute('role', 'group');
    });
  }

  function isVisibleFocusable(el) {
    if (!el || el.disabled) return false;
    if (el.getAttribute('aria-hidden') === 'true') return false;
    if (el.tabIndex === -1) return false;

    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return false;

    if (el.tagName === 'A' && el.closest('.shopify-payment-button')) {
      const label = (el.getAttribute('aria-label') || el.textContent || '').trim();
      if (!label || label === '.') return false;
      if (el.offsetHeight === 0 && el.offsetWidth === 0) return false;
    }

    return true;
  }

  function getVisibleFocusables(scope) {
    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([type=hidden]):not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
      'iframe',
      'summary'
    ].join(', ');

    return Array.from(scope.querySelectorAll(selector)).filter(isVisibleFocusable);
  }

  function getAdjacentFocusable(element, direction) {
    const focusables = getVisibleFocusables(document);
    const index = focusables.indexOf(element);
    if (index === -1) return null;

    return focusables[index + direction] || null;
  }

  function fixHiddenViewCartTabStop(root) {
    const scope = root || document;

    scope.querySelectorAll('a.view-cart.hide').forEach(function (link) {
      link.setAttribute('tabindex', '-1');
      link.setAttribute('aria-hidden', 'true');
    });
  }

  function fixDpoDuplicateAtcButtons(root) {
    const scope = root || document;

    scope.querySelectorAll('.product-form__buttons').forEach(function (buttons) {
      const hiddenOriginal = buttons.querySelector(
        'button.product-form__submit:not([dpo_add2cart])'
      );

      if (!hiddenOriginal) return;

      const style = window.getComputedStyle(hiddenOriginal);
      if (style.display === 'none' || style.visibility === 'hidden') {
        hiddenOriginal.setAttribute('tabindex', '-1');
        hiddenOriginal.setAttribute('aria-hidden', 'true');
        hiddenOriginal.removeAttribute('id');
      }
    });

    scope.querySelectorAll('.shopify-payment-button[tabindex="-1"]').forEach(function (payment) {
      payment.removeAttribute('tabindex');
    });

    scope.querySelectorAll('button.product-form__submit[data-a11y-tab-fix]').forEach(function (button) {
      button.removeAttribute('data-a11y-tab-fix');
    });
  }

  function fixShopifyPaymentTabStops(root) {
    const scope = root || document;

    scope.querySelectorAll('.shopify-payment-button a[href]').forEach(function (link) {
      const label = (link.getAttribute('aria-label') || link.textContent || '').trim();
      const isPlaceholder = !label || label === '.';
      const hasNoVisibleBox = link.offsetHeight === 0;

      if (isPlaceholder || hasNoVisibleBox) {
        link.setAttribute('tabindex', '-1');
        link.setAttribute('aria-hidden', 'true');
      }
    });
  }

  let productFormTabNavigationBound = false;

  function bindProductFormTabNavigation() {
    if (productFormTabNavigationBound) return;
    productFormTabNavigationBound = true;

    document.addEventListener(
      'keydown',
      function (event) {
        if (event.key !== 'Tab' && event.code !== 'Tab') return;

        const target = event.target;
        if (!target || !target.matches('button.product-form__submit')) return;

        const buttons = target.closest('.product-form__buttons');
        if (!buttons) return;

        const isDpoButton = target.hasAttribute('dpo_add2cart');
        const hasHiddenViewCart = !!buttons.querySelector('a.view-cart.hide');
        if (!isDpoButton && !hasHiddenViewCart) return;

        const direction = event.shiftKey ? -1 : 1;
        const next = getAdjacentFocusable(target, direction);
        if (!next || next === target) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        next.focus();
      },
      true
    );
  }

  function restoreProductGalleryListSemantics(root) {
    const scope = root || document;

    scope.querySelectorAll('[id^="Slider-Gallery"]').forEach(function (slider) {
      slider.removeAttribute('data-a11y-scroll-keys-bound');
      slider.removeAttribute('tabindex');

      const role = slider.getAttribute('role');
      if (role === 'region' || role === 'presentation' || role === 'group') {
        slider.setAttribute('role', 'list');
      }

      slider.querySelectorAll('[id^="Slide-"]').forEach(function (slide) {
        const slideRole = slide.getAttribute('role');
        if (slideRole === 'group' || slideRole === 'presentation' || slideRole === 'region') {
          slide.removeAttribute('role');
        }
      });
    });
  }

  function setInertState(element, shouldBeInert) {
    if (!element) return;
    if (shouldBeInert) {
      element.setAttribute('inert', '');
    } else {
      element.removeAttribute('inert');
    }
  }

  function bindSkipLinks() {
    document.querySelectorAll('a.skip-to-content-link[href^="#"]').forEach(function (link) {
      if (link.dataset.a11ySkipBound === 'true') return;
      link.dataset.a11ySkipBound = 'true';

      link.addEventListener('click', function (event) {
        const targetId = link.getAttribute('href')?.slice(1);
        if (!targetId) return;

        const target = document.getElementById(targetId);
        if (!target) return;

        event.preventDefault();
        if (!target.hasAttribute('tabindex')) {
          target.setAttribute('tabindex', '-1');
        }
        target.focus({ preventScroll: false });
        target.scrollIntoView({ block: 'start' });
      });
    });
  }

  function manageOverlayInert(root) {
    const scope = root || document;

    scope.querySelectorAll('#exit-popup, #email-popup, #Purchase-popup, [id^="dy-popup"]').forEach(function (popup) {
      const style = window.getComputedStyle(popup);
      const hidden =
        style.display === 'none' ||
        style.visibility === 'hidden' ||
        Number(style.opacity) === 0;
      setInertState(popup, hidden);
    });

    scope.querySelectorAll('modal-dialog:not([open])').forEach(function (dialog) {
      setInertState(dialog, true);
    });

    scope.querySelectorAll('modal-dialog[open]').forEach(function (dialog) {
      setInertState(dialog, false);
    });
  }

  let skipLinksBound = false;

  function runPatches(root) {
    restoreProductGalleryListSemantics(root);
    fixEasytabsEmbeddedRegions(root);
    fixCartLineItemImageAlts(root);
    enhanceMerchantWidgetIframe();
    fixHiddenViewCartTabStop(root);
    fixDpoDuplicateAtcButtons(root);
    fixShopifyPaymentTabStops(root);
    bindProductFormTabNavigation();
    manageOverlayInert(root);

    if (!skipLinksBound) {
      bindSkipLinks();
      skipLinksBound = true;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      runPatches(document);
    });
  } else {
    runPatches(document);
  }

  const observer = new MutationObserver(function () {
    runPatches(document);
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
